import { browser } from '$app/environment';
import { writable } from 'svelte/store';

import { operations, transactions } from '$lib/constants/indexedDB-operations';
import type { NewsCDS, News } from '$lib/types/news';
import { getDB, performDBOperation } from '$lib/utils/indexedDB';
import Logger from '$lib/utils/logger';

export type BookmarkType = (NewsCDS | News) & { id: string };

export const bookmarks = writable<BookmarkType[]>([]);

const dbName = 'BookmarksDB';
const storeName = 'bookmarks';

const updateBookmarksStore = (action: operations, data: BookmarkType | string) => {
  bookmarks.update((currentBookmarks) =>
    action === operations.ADD
      ? [...currentBookmarks, data as BookmarkType]
      : currentBookmarks.filter((item) => item?.id !== (data as string))
  );
};

export const addBookmark = async (newsItem: BookmarkType): Promise<BookmarkType | News> => {
  try {
    await performDBOperation(operations.ADD, newsItem, dbName, storeName);
    updateBookmarksStore(operations.ADD, newsItem);
    return newsItem;
  } catch (error) {
    Logger.error('Error adding bookmark:', error);
    throw error; // Re-throw the error for proper error handling at a higher level
  }
};

export const removeBookmark = async (id: string): Promise<string> => {
  try {
    await performDBOperation(operations.REMOVE, id, dbName, storeName);
    updateBookmarksStore(operations.REMOVE, id);
    return id;
  } catch (error) {
    Logger.error('Error removing bookmark:', error);
    throw error;
  }
};

export const loadBookmarks = async (): Promise<void> => {
  if (!browser) return;
  const db = await getDB(dbName, storeName);
  const store = db.transaction(storeName, transactions.READ_ONLY).objectStore(storeName);
  try {
    const bookmarksArray: BookmarkType[] = await store.getAll();
    bookmarks.set(bookmarksArray);
  } catch (error) {
    Logger.error('Error loading bookmarks:', error);
    throw error;
  }
};

export const isBookmarkInIndexedDB = async (id: string): Promise<boolean> => {
  if (!browser) return false;
  if (!id) {
    Logger.error('Invalid bookmark key:', id);
    return false;
  }
  const db = await getDB(dbName, storeName);
  const store = db.transaction(storeName, transactions.READ_ONLY).objectStore(storeName);

  try {
    const bookmark = await store.get(id);
    return bookmark !== undefined;
  } catch (error) {
    Logger.error('Error checking bookmark in IndexedDB:', error);
    throw error;
  }
};
